import { useMemo } from 'react';

import {
  flattenWorkspaceAgentBoardTree,
  toWorkspaceAgentBoardTreeBrowseLevel,
} from '~/components/v2/components/workspace/workspace-agent-panel/mentions/flattenWorkspaceAgentBoardTree';
import {
  type WorkspaceAgentMentionSearchParams,
  type WorkspaceAgentMentionSearchResult,
} from '~/components/v2/components/workspace/workspace-agent-panel/mentions/types';
import {
  useWorkspaceAgentMentionSearchQueries,
  useWorkspaceAgentMentionSearchResult,
} from '~/components/v2/components/workspace/workspace-agent-panel/mentions/useWorkspaceAgentMentionSearchResult';
import {
  WORKSPACE_AGENT_BOARD_LIST_OPTIONS,
  WORKSPACE_AGENT_MENTION_PAGE_SIZE,
} from '~/components/v2/components/workspace/workspace-agent-panel/mentions/workspaceAgentMentionConstants';
import { usePrivateBoardsList } from '~/components/v2/hooks/queries/usePrivateBoardsList';

/** Nothing is fetched under a query, so a matched board is drawn without children of its own. */
const NO_FETCHED_CHILDREN = {};

/** Flat results are still tree rows, so nothing is expanded and every row sits at the root. */
const NOTHING_EXPANDED = () => false;

export type PrivateWorkspaceAgentBoardMentionSearchResult = WorkspaceAgentMentionSearchResult & {
  /** Same term the request is keyed on, so title-match fallbacks do not race the live input. */
  debouncedQuery: string;
};

/** Workspace-wide, every board the user can view: a mention is a reference, not a destination. */
export const usePrivateWorkspaceAgentBoardMentionSearch = ({
  enabled,
  query,
  workspaceId,
}: WorkspaceAgentMentionSearchParams): PrivateWorkspaceAgentBoardMentionSearchResult => {
  const queries = useWorkspaceAgentMentionSearchQueries({ enabled, query });
  const { debouncedQuery } = queries;

  const search = usePrivateBoardsList(
    {
      includeCoverImage: WORKSPACE_AGENT_BOARD_LIST_OPTIONS.includeCoverImage,
      isMemberOfBoard: WORKSPACE_AGENT_BOARD_LIST_OPTIONS.isMemberOfBoard,
      // Every library's boards as well as the workspace's own. No child count: nothing is fetched under a query.
      libraryBoards: 'ALL',
      limit: WORKSPACE_AGENT_MENTION_PAGE_SIZE,
      search: debouncedQuery || undefined,
      view: 'all',
      workspaceId,
    },
    // Debounced term, not the live query: enabling on the first keystroke would fire `view: 'all'`
    // for 300ms with no search string.
    { enabled: enabled && !!workspaceId && !!debouncedQuery },
  );

  const { data, hasNextPage } = search;

  const items = useMemo(() => {
    if (!data) {
      return undefined;
    }

    // Placed by the same flattener the browsed tree uses: a row is only ever drawn once something
    // has said where it sits, and a flat list is the one-level case of that.
    return flattenWorkspaceAgentBoardTree(
      toWorkspaceAgentBoardTreeBrowseLevel(
        data.pages.flatMap((page) => page.data),
        NO_FETCHED_CHILDREN,
        false,
      ),
      NOTHING_EXPANDED,
      !!hasNextPage,
    );
  }, [data, hasNextPage]);

  const result = useWorkspaceAgentMentionSearchResult({ items, queries, search });

  return useMemo(() => ({ ...result, debouncedQuery }), [debouncedQuery, result]);
};
